import { JobFailure } from '../publish';
import { WorkflowStartResult } from '../external';
import { SocketMessage, SocketMessageEvent } from './messages';
import { SocketIOSocket } from './typings';

export interface SocketEventHandler<T> {
    // eslint-disable-next-line @typescript-eslint/prefer-function-type, @typescript-eslint/no-unsafe-function-type
    (result: T, acknowledge?: Function): any;
}

export interface BasicWorkflowProgressMessage {
    executionId?: string;
    timestamp: number;
}

export abstract class BaseSocketClientService {
    protected socket: SocketIOSocket | undefined;

    protected isLoggedIn = false;

    private readonly ALERT_LIMIT_MILLISECONDS = 600000; // 10 minutes

    public constructor(protected readonly promiseConstructor: PromiseConstructorLike) {}

    /**
     * Call this method to create the socket
     */
    public async initialise(): Promise<void> {
        // If we already have a socket, don't re-initialise
        if (this.socket) {
            return;
        }

        // Create the socket
        this.socket = this.createSocket();

        // Run the onSocketCreated method
        this.onSocketCreated();

        this.isLoggedIn = await this.getInitialLoggedInStatus();

        if (this.isLoggedIn) {
            this.reconnect();
        }

        this.startWatchers(
            () => this.onAuthenticated(),
            () => this.onUnauthenticated(),
        );
    }

    /**
     * Forcefully disconnects socket. Calling disconnect will stop socket from automatically reconnecting.
     */
    public disconnect(): void {
        if (!this.socket) {
            return;
        }

        this.socket.disconnect();
    }

    /**
     * Forcefully disconnects and reconnects the socket
     */
    public reconnect(): void {
        if (!this.socket) {
            return;
        }

        this.disconnect();
        this.socket.connect();
    }

    public on<T>(eventType: SocketMessageEvent<T>, handler: SocketEventHandler<T>): void {
        if (!this.socket) {
            return;
        }

        this.socket.on(eventType.name, handler);
    }

    public onIf<T>(
        eventType: SocketMessageEvent<T>,
        handler: SocketEventHandler<T>,
        predicate: (data: T) => boolean,
    ): void {
        if (!this.socket) {
            return;
        }

        // eslint-disable-next-line consistent-return
        const outerHandler: SocketEventHandler<T> = (result, ack?) => {
            if (predicate(result)) {
                return handler(result, ack);
            }
        };
        this.socket.on(eventType.name, outerHandler);
        // TODO: fix "off" for all outerHandler stuff. Need to map original handler to outerHandler.
    }

    public off<T>(eventType: SocketMessageEvent<T>, handler: SocketEventHandler<T>): void {
        if (!this.socket) {
            return;
        }

        this.socket.off(eventType.name, handler);
    }

    public once<T>(eventType: SocketMessageEvent<T>, handler: SocketEventHandler<T>): void {
        if (!this.socket) {
            return;
        }

        const outerHandler: SocketEventHandler<T> = (result, ack?) => {
            if (this.socket) {
                this.socket.off(eventType.name, outerHandler);
            }
            handler(result, ack);
        };
        this.socket.on(eventType.name, outerHandler);
    }

    public onceIf<T extends { executionId?: string }>(
        eventType: SocketMessageEvent<T>,
        handler: SocketEventHandler<T>,
        predicate: (data: T) => boolean,
    ): void {
        if (!this.socket) {
            return;
        }

        const outerHandler: SocketEventHandler<T> = (result, ack?) => {
            if (!predicate(result)) {
                // Return early
                return;
            }

            if (this.socket) {
                this.socket.off(eventType.name, outerHandler);
            }
            handler(result, ack);
        };
        this.socket.on(eventType.name, outerHandler);
    }

    public emit<T>(message: SocketMessage<T>): void {
        // We use SocketMessage for parity with server-side message emission.
        if (this.socket) {
            if (this.isLoggedIn && this.socket.connected) {
                // Only support sending one arg; easier to validate and pass around.
                this.socket.emit(message.name, message.data);
            }
            // Should we handle failure scenario?
        }
    }

    /**
     * Triggers a Step Function workflow. Listens for success, error, and optionally progress events. Filters events by
     * the execution ID.
     *
     * @param workflowTrigger -
     * @param successEvent -
     * @param errorEvent -
     * @param progress -
     * @param progress.progressEvent -
     * @param progress.onProgress -
     * @param onAlert - an optional callback for when no expected socket event is received after
     * ALERT_LIMIT_MILLISECONDS
     * @returns - The promise with TSuccess
     */
    public startWorkflow<
        TSuccess extends { executionId?: string },
        TError extends { executionId?: string },
        TProgress extends BasicWorkflowProgressMessage,
    >(
        workflowTrigger: () => PromiseLike<WorkflowStartResult>,
        successEvent: SocketMessageEvent<TSuccess>,
        errorEvent: SocketMessageEvent<TError>,
        progress?: {
            progressEvent: SocketMessageEvent<TProgress>;
            onProgress: (result: TProgress) => void;
        },
        onAlert?: () => void,
    ): PromiseLike<TSuccess> {
        let executionId: string | undefined;
        let lastProgressTimestamp = new Date().getTime();
        let alertTimeout: ReturnType<typeof setTimeout>;

        const messageFilter = (data: { executionId?: string }) => data.executionId === executionId;
        // eslint-disable-next-line id-length
        const p = new this.promiseConstructor((resolve: (result: TSuccess) => any, reject: (result: TError) => any) => {
            const removeSocketListeners = () => {
                this.off(successEvent, finishedHandler);
                this.off(errorEvent, errorHandler);
                if (progress) {
                    this.off(progress.progressEvent, progressHandler);
                }
                clearTimeout(alertTimeout);
            };
            const finishedHandler = (result: TSuccess) => {
                removeSocketListeners();
                return resolve(result);
            };
            const errorHandler = (result: TError) => {
                removeSocketListeners();
                return reject(result);
            };
            let progressHandler: (result: TProgress) => void;
            if (progress) {
                progressHandler = (result: TProgress) => {
                    lastProgressTimestamp = result.timestamp;
                    progress.onProgress(result);
                };
            }
            const addSocketListeners = () => {
                this.onceIf(successEvent, finishedHandler, messageFilter);
                this.onceIf(errorEvent, errorHandler, messageFilter);
                if (progress) {
                    this.onIf(
                        progress.progressEvent,
                        progressHandler,
                        event => messageFilter(event) && event.timestamp >= lastProgressTimestamp,
                    );
                }
                if (onAlert) {
                    alertTimeout = setTimeout(onAlert, this.ALERT_LIMIT_MILLISECONDS);
                }
            };

            addSocketListeners();
        });
        return workflowTrigger().then(result => {
            executionId = result.executionId;
            return p;
        });
    }

    // eslint-disable-next-line max-params
    public async startWorkflowV2<
        WorkflowReturn extends { executionId: WorkflowStartResult['executionId'] },
        TSuccess extends { executionId: string },
        TError extends JobFailure,
    >(
        workflowTrigger: () => PromiseLike<WorkflowReturn[]>,
        successEvent: SocketMessageEvent<TSuccess>,
        errorEvent: SocketMessageEvent<TError>,
        success: (response: TSuccess, workflowData: WorkflowReturn) => void,
        error: (response: TError, workflowData: WorkflowReturn) => void,
        onCompleteAll?: () => void,
        onAlert?: () => void,
    ): Promise<WorkflowReturn[]> {
        let alertTimeout: ReturnType<typeof setTimeout>;

        const remainingData: WorkflowReturn[] = [];

        const finishCheck = () => {
            if (!remainingData.length) {
                removeListeners();
                onCompleteAll?.();
            }
        };
        const successHandler = (response: TSuccess) => {
            const index = remainingData.findIndex(data => data.executionId === response.executionId);

            if (index >= 0) {
                success(response, remainingData[index]);

                remainingData.splice(index, 1);
                finishCheck();
            }
        };
        const errorHandler = (response: TError) => {
            const index = remainingData.findIndex(data => data.executionId === response.executionId);

            if (index >= 0) {
                error(response, remainingData[index]);

                remainingData.splice(index, 1);
                finishCheck();
            }
        };

        const addListeners = () => {
            this.on(successEvent, successHandler);
            this.on(errorEvent, errorHandler);
            if (onAlert) {
                alertTimeout = setTimeout(onAlert, this.ALERT_LIMIT_MILLISECONDS);
            }
        };

        const removeListeners = () => {
            this.off(successEvent, successHandler);
            this.off(errorEvent, errorHandler);
            clearTimeout(alertTimeout);
        };

        addListeners();

        const executionData = await workflowTrigger();
        remainingData.push(...executionData);

        return executionData;
    }

    protected abstract createSocket(): SocketIOSocket;

    protected abstract getInitialLoggedInStatus(): Promise<boolean>;

    protected abstract startWatchers(onAuthenticated: () => void, onUnauthenticated: () => void): void;

    protected onAuthenticated(): void {
        this.isLoggedIn = true;
        this.reconnect();
    }

    protected onUnauthenticated(): void {
        this.isLoggedIn = false;
        this.disconnect();
    }

    /**
     * Initialises the socket listeners. This is only ever called once when you create the socket.
     * If you reconnect it will not be called again, so don't worry
     */
    protected onSocketCreated(): void {
        if (!this.socket) {
            return;
        }
        // Register some listeners
        this.socket.on('connect', () => this.onConnected());
        this.socket.on('disconnect', () => this.onDisconnected());
    }

    protected onConnected(): void {
        // Do nothing
    }

    protected onDisconnected(): void {
        // Do nothing
    }
}
